<template>
  <div class="column-wrapper">
    <div class="column">
      <div v-if="isEditable" class="pb-1 header">
        <assortment-text-field
          v-model="name"
          width="140px"
          class="text-field"
          :rules="rules"
          variant="underlined"
          placeholder="Name"
          @update:model-value="updateName"
        />
        <v-icon class="mt-2" size="22" @click="deleteGroup"> $trash </v-icon>
      </div>
      <div v-else class="pb-1 available-values-header header">
        {{ tkey('availableValues') }}
      </div>
      <div v-if="attrGroup.values" class="list-group ma-0 pa-0">
        <draggable :list="attrGroup.values" group="attrs" :item-key="itemKey">
          <template #item="{ element: attrValue }">
            <div class="pl-2 d-flex align-center justify-start attribute-item w-100">
              {{ attrValue }}
              <div class="icon mt-1">
                <v-icon
                  v-if="isEditable"
                  size="14"
                  :title="tkey('removeAttribute')"
                  @click="deleteAttr(attrValue)"
                >
                  mdi-close
                </v-icon>
                <v-icon size="10"> $cross-move </v-icon>
              </div>
            </div>
          </template>
          <template #footer>
            <div class="pl-2 pr-2 d-flex align-center justify-start attribute-item drag-here">
              {{ tkey('dragValuesHere') }}
            </div>
            <v-alert v-if="!attrGroup.values.length && !isEditable" type="info" text>
              {{ tkey('noRemainingAttributes') }}
            </v-alert>
          </template>
        </draggable>
      </div>
    </div>
    <div class="btn-wrapper">
      <v-btn
        v-if="isEditable"
        variant="text"
        class="ma-2"
        :disabled="disableAddAllRemaining"
        @click="addAllRemaining"
      >
        {{ tkey('addAllRemainingValues') }}
      </v-btn>
      <v-btn
        v-if="!isEditable"
        :disabled="disableAddAllRemaining"
        variant="text"
        class="ma-2"
        @click="distributeRemaining"
      >
        {{ tkey('distributeRemainingValues') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { useInternationalization } from '@/composables/use-internationalization';

export default {
  props: {
    attrGroup: {
      type: Object,
      required: true,
    },
    isEditable: {
      type: Boolean,
      default: () => true,
    },
    disableAddAllRemaining: {
      type: Boolean,
      required: true,
    },
    reservedNames: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { tkey } = useInternationalization('scenarioInputPage.attributeGroupingModal');
    return { tkey };
  },

  data() {
    return {
      name: '',
      rules: [this.isUniqueGroupName],
    };
  },

  created() {
    this.name = this.attrGroup.name;
  },
  methods: {
    // Required as :item-key="element" will result in a value of `undefined` for the item-key
    itemKey(element) {
      return element;
    },
    deleteAttr(attrValue) {
      this.$emit('delete-attr', { groupId: this.attrGroup.id, attrValue });
    },
    deleteGroup() {
      this.$emit('delete-group', this.attrGroup.id);
    },
    addAllRemaining() {
      this.$emit('add-all-remaining', this.attrGroup.id);
    },
    distributeRemaining() {
      this.$emit('distribute-remaining');
    },
    updateName() {
      this.attrGroup.name = this.name;
      this.$emit('update', this.attrGroup);
    },
    isUniqueGroupName(value) {
      this.$emit('validation', { id: this.attrGroup.id, isValid: false });
      if (!value) return this.tkey('groupNameIsRequired');
      const isTaken = this.reservedNames.some(n => n.id !== this.attrGroup.id && n.name === value);

      if (isTaken) {
        return this.tkey('groupNameHasToBeUnique');
      }
      this.$emit('validation', { id: this.attrGroup.id, isValid: true });
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.v-alert) {
  font-size: 1.2rem !important;
  padding: 7px 5px;

  .v-icon {
    margin-right: 5px !important;
  }
}

:deep(.icon) {
  order: 2;
  margin: auto 0 0 auto;
  flex-shrink: 0;
  align-self: flex-start;
}

.v-btn {
  color: $assortment-primary-colour;
}

.available-values-header {
  font-weight: bold;
  font-size: 1.3rem;
}

.header {
  display: flex;
}

.column-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.btn-wrapper {
  flex-grow: 0;
}

.column {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.list-group {
  overflow: auto !important;
  padding: 0 !important;

  .attribute-item {
    height: 28px;
    width: 100%;
    padding-left: 0px;
    margin-bottom: 5px;
    border-radius: 3px;
    color: $assortment-text-colour;
    border: 1px solid transparent;
    background-color: $assortment-attribute-colour;
    font-weight: bold;
  }

  .drag-here {
    background-color: $assortment-background !important;
    color: $assortment-greyed-out-colour;
  }
}
</style>
